import type { CalendarDate } from '@internationalized/date';
import {
  CalendarStateContext,
  RangeCalendarStateContext,
} from 'react-aria-components';
import {
  type ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDateFormatter } from 'react-aria';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { ChevronDownIcon } from 'lucide-react';
import { cn } from './helpers';

interface YearItem {
  id: number;
  date: CalendarDate;
  formatted: string;
}

export const useScrollToSelectedElement = (isOpen: boolean) => {
  const selectedElRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        if (!containerRef.current || !selectedElRef.current) return;

        const container = containerRef.current;
        const selectedElement = selectedElRef.current;

        const containerHeight = container.clientHeight;
        const elementHeight = selectedElement.offsetHeight;
        const elementOffsetTop = selectedElement.offsetTop;

        const scrollTop =
          elementOffsetTop - containerHeight / 2 + elementHeight / 2;

        container.scrollTo({
          top: scrollTop,
        });
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  return { containerRef, selectedElRef };
};

export function YearDropdown(): ReactElement {
  const calendarState = useContext(CalendarStateContext);
  const rangeCalendarState = useContext(RangeCalendarStateContext);
  const state = calendarState || rangeCalendarState!;

  const formatter = useDateFormatter({
    year: 'numeric',
    timeZone: state.timeZone,
  });

  const currentYear = new Date().getFullYear();
  const startYear = 1900;
  const endYear = calendarState?.maxValue?.year ?? currentYear + 10;

  const years: YearItem[] = [];
  for (let year = endYear; year >= startYear; year--) {
    const date = state.focusedDate.set({ year });
    years.push({
      id: years.length,
      date,
      formatted: formatter.format(date.toDate(state.timeZone)),
    });
  }

  const [isOpen, setIsOpen] = useState(false);

  const { containerRef, selectedElRef } = useScrollToSelectedElement(isOpen);

  return (
    <DropdownMenu
      aria-label='Year'
      modal={false}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DropdownMenuTrigger
        onClick={() => setIsOpen(true)}
        className='hover:bg-primary-100 data-[state=open]:bg-primary-100 font-medium flex items-center gap-1 rounded-md h-9 px-2 cursor-pointer'
      >
        {state.focusedDate.year} <ChevronDownIcon size={16} />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        ref={containerRef}
        align='start'
        style={{ scrollbarWidth: 'none' }}
        className='z-[100] h-[200px] overflow-y-auto'
      >
        {years.map((item) => {
          const isSelected = item.date.year === state.focusedDate.year;
          return (
            <DropdownMenuItem
              ref={isSelected ? selectedElRef : undefined}
              onClick={() => state.setFocusedDate(item.date)}
              className={cn(
                isSelected &&
                  'bg-primary-100 hover:bg-primary-100 text-primary-900 font-medium'
              )}
              key={item.id}
            >
              {item.formatted}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
